import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis,
  CartesianGrid, Tooltip, ReferenceLine, ReferenceArea,
} from 'recharts';

// Canal palette — rich, modern, distinguishable (no raw primaries).
export const CANAL_COLORS = {
  'Porte à Porte': '#E8833A',
  'Rue Paris IDF': '#3E7CB1',
  'Rue itinérante': '#2E9E8F',
  'Sites privés': '#8E6FB0',
};
const DEFAULT_COLOR = '#8896A6';

const median = (arr) => {
  const s = arr.filter((v) => typeof v === 'number').sort((a, b) => a - b);
  if (!s.length) return 0;
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const BubbleTooltip = ({ active, payload, nameKey, subKey, fields }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  if (!d) return null;
  return (
    <div className="max-w-[250px] rounded-2xl glass-strong card-shadow-lg px-3 py-2">
      <div className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: d._color }} />
        <span className="break-words text-xs font-semibold text-foreground">{d[nameKey]}</span>
      </div>
      {subKey && d[subKey] ? <div className="mt-0.5 break-words text-[10px] text-muted-foreground">{d[subKey]}</div> : null}
      <div className="mt-1.5 space-y-0.5">
        {fields.map((f) => (
          <div key={f.label} className="flex items-center gap-3 text-xs">
            <span className="text-muted-foreground">{f.label}</span>
            <span className="ml-auto tabular font-medium text-foreground">{f.format(d[f.key])}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Performance map: x = volume, y = quality, bubble size = value (z), colour = group.
 * Median reference lines split the plane into 4 quadrants; the top-right ("stars")
 * quadrant is softly highlighted. Nothing is truncated; large datasets should be
 * capped by the caller (top-N by volume) before being passed in.
 */
export const PerformanceBubble = ({
  data = [], xKey, yKey, zKey, nameKey = 'name', subKey, groupKey,
  colorMap = CANAL_COLORS, xLabel, yLabel, height = 440,
  formatX = (v) => v, formatY = (v) => v, formatZ = (v) => v, extraFields = [],
}) => {
  const rows = data.map((d) => ({
    ...d,
    _color: colorMap[groupKey ? d[groupKey] : ''] || DEFAULT_COLOR,
  }));
  const mx = median(rows.map((d) => d[xKey] || 0));
  const my = median(rows.map((d) => d[yKey] || 0));
  const maxX = Math.max(...rows.map((d) => d[xKey] || 0), 1);
  const maxY = Math.max(...rows.map((d) => d[yKey] || 0), 0.0001);

  const groups = {};
  rows.forEach((d) => {
    const g = groupKey ? (d[groupKey] || '—') : 'Tous';
    (groups[g] = groups[g] || []).push(d);
  });
  const cats = Object.keys(groups);

  const fields = [
    { key: xKey, label: xLabel, format: formatX },
    { key: yKey, label: yLabel, format: formatY },
    { key: zKey, label: 'Montant', format: formatZ },
    ...extraFields,
  ];

  return (
    <div data-testid="performance-bubble">
      {/* Legend */}
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {cats.map((c) => (
          <span key={c} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: colorMap[c] || DEFAULT_COLOR }} />{c}
          </span>
        ))}
        <span className="ml-auto text-[10px] text-muted-foreground/70">Taille des bulles = montant récurrent</span>
      </div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <ScatterChart margin={{ top: 16, right: 24, bottom: 40, left: 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(11,19,32,0.06)" />
            <XAxis type="number" dataKey={xKey} name={xLabel} tickFormatter={formatX}
                   tickLine={false} axisLine={false} tick={{ fontSize: 11 }} domain={[0, 'dataMax']}
                   label={{ value: xLabel, position: 'insideBottom', offset: -20, fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis type="number" dataKey={yKey} name={yLabel} tickFormatter={formatY}
                   tickLine={false} axisLine={false} tick={{ fontSize: 11 }} domain={[0, 'dataMax']}
                   label={{ value: yLabel, angle: -90, position: 'insideLeft', offset: 16, fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
            <ZAxis type="number" dataKey={zKey} range={[70, 820]} name="Montant" />
            <ReferenceArea x1={mx} x2={maxX} y1={my} y2={maxY} fill="var(--tenant-accent-500)" fillOpacity={0.11} stroke="var(--tenant-accent-400)" strokeOpacity={0.35} strokeDasharray="2 3"
                           label={{ value: '★ Stars', position: 'insideTopRight', fontSize: 11, fontWeight: 600, fill: 'var(--tenant-accent-600)' }} />
            <ReferenceLine x={mx} stroke="rgba(11,19,32,0.18)" strokeDasharray="4 4" />
            <ReferenceLine y={my} stroke="rgba(11,19,32,0.18)" strokeDasharray="4 4" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }}
                     content={<BubbleTooltip nameKey={nameKey} subKey={subKey} fields={fields} />} />
            {cats.map((cat) => (
              <Scatter key={cat} name={cat} data={groups[cat]} fill={colorMap[cat] || DEFAULT_COLOR}
                       fillOpacity={0.72} stroke="#ffffff" strokeWidth={1} />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
